import {PgForm} from '@practicegenius/domain/src/hooks/usePgForm';
import * as R from 'ramda';
import * as Yup from 'yup';
import React from 'react';
import {dropNilOrEmptyValues} from '@practicegenius/pg-util/src/ObjectUtils';
import FormHelperText from '@mui/material/FormHelperText';
import {InvariantError} from 'ts-invariant';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import {format, isValid, parse} from 'date-fns';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import {CommonDateFormats} from '@practicegenius/pg-util/src/DateUtils';

/** Representation of a date's components. For maximum flexibility these are internally represented as strings and  */
export type MemorableDate = {
  month: string;
  day: string;
  year: string;
};

const getMonthsForLocale = (locale = navigator.language) => {
  const format = new Intl.DateTimeFormat(locale, {month: 'long'});
  const months = [];
  for (let month = 0; month < 12; month++) {
    const testDate = new Date(0, month, 1, 0, 0, 0);
    months.push(format.format(testDate));
  }
  return months;
};

const localeMonths = getMonthsForLocale();

export const parseMemorableDateFieldsFromDate = (date: Date): MemorableDate =>
  isValid(date)
    ? {
        month: String(date.getMonth() + 1),
        day: String(date.getDate()),
        year: String(date.getFullYear()),
      }
    : {
        month: '',
        day: '',
        year: '',
      };

export const parseDateFromMemorableDate = (
  memDate: MemorableDate$Form,
): Date | null => {
  const parseValue = (value: string) => parseInt(value, 10);
  const parsed = R.mapObjIndexed(parseValue, R.omit(['composedDate'], memDate));
  if (Object.values(parsed).some(Number.isNaN)) return null;

  // Parse the date with date-fns to avoid strange edge cases with the Date constructor (e.g. 2021-02-31 -> 2021-03-03)
  return parse(
    `${parsed.year}-${parsed.month}-${parsed.day}`,
    'y-M-d',
    new Date(),
  );
};

type SubfieldProps = {
  value: string;
  name: string;
  onChange: React.ChangeEventHandler;
  onBlur: React.FocusEventHandler;
  /** Subfield errors are joined together if there are multiple */
  error?: string;
};
type MemorableDateInputProps = {
  /** id used as a prefix for subfield ids */
  fieldId: string;
  fields: Record<keyof MemorableDate, SubfieldProps>;
  /** A field-level error will only render if no field level errors are present. It's only expected that this will happen for server errors that can't be validated on the client */
  error?: string;
  /** This is added to the month field to inform the user of the date's context */
  monthFieldPrefix?: string;
};

const getFieldError = ({
  error,
  fields,
}: Pick<MemorableDateInputProps, 'error' | 'fields'>) => {
  const errors = dropNilOrEmptyValues([
    ...Object.values(fields).map(field => field.error),
  ]).filter(Boolean) as string[];
  const hasFieldErrors = errors.length > 0;
  if (!hasFieldErrors) return error;
  return errors.join(', ');
};

/**
 * A user-friendly input for memorable dates (such as birthdays or anniversaries).
 * This component implements the best practices for prompting users for memorable dates as outlined by the USWDS
 * @see https://designsystem.digital.gov/patterns/create-a-user-profile/date-of-birth/
 */
export const MemorableDateInput: React.FC<MemorableDateInputProps> = ({
  fieldId,
  fields,
  error,
  monthFieldPrefix,
}) => {
  const fieldError = getFieldError({error, fields});
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
        }}
      >
        {/* Month Field */}
        {(() => {
          let labelText = 'Month';
          if (monthFieldPrefix) labelText = `${monthFieldPrefix} ${labelText}`;

          return (
            <FormControl
              fullWidth
              sx={{
                width: '50%',
              }}
              error={Boolean(error || fields.month.error)}
            >
              <InputLabel id="relationship-label">{labelText}</InputLabel>
              <Select
                labelId="relationship-label"
                id="relationship"
                label={labelText}
                name={fields.month.name}
                value={fields.month.value}
                onChange={e =>
                  // @ts-expect-error this works fine but Select's types are a little weird
                  fields.month.onChange(e)
                }
                onBlur={fields.month.onBlur}
                error={Boolean(error || fields.month.error)}
                MenuProps={{
                  sx: {
                    '& .MuiList-root': {
                      maxHeight: '280px',
                    },
                  },
                }}
              >
                {localeMonths.map((label, idx) => {
                  const monthNum = String(idx + 1);
                  return (
                    <MenuItem key={label} value={String(monthNum)}>
                      {monthNum} - {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          );
        })()}

        {/* Day Field */}
        <TextField
          id={`${fieldId}-day`}
          label="Day"
          placeholder="DD"
          {...R.omit(['error'], fields.day)}
          error={Boolean(error || fields.day.error)}
          sx={{
            width: '20%',
          }}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: 2,
          }}
        />

        {/* Year Field */}
        <TextField
          id={`${fieldId}-year`}
          label="Year"
          placeholder="YYYY"
          {...R.omit(['error'], fields.year)}
          error={Boolean(error || fields.year.error)}
          sx={{
            width: '30%',
          }}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: 4,
          }}
        />
      </Box>
      <FormHelperText error>{fieldError}</FormHelperText>
    </Box>
  );
};

type MemorableDateValidationSchemaInput = {
  /**
   * Add additional validation rules to the composed date
   * @default R.identity
   *  */
  enhanceDateSchema?: (input: Yup.DateSchema) => Yup.DateSchema;
};

const memorableDateSchema = Yup.object().shape({
  month: Yup.number()
    .typeError('Month must be a number')
    .min(1, 'Month must be greater than 0')
    .max(12, 'Month cannot exceed 12')
    .integer('Month must be a whole number')
    .required('Month is required'),
  day: Yup.number()
    .typeError('Day must be a number')
    .min(1, 'Day must be greater than 0')
    .max(31, 'Day cannot exceed 31')
    .integer('Day must be a whole number')
    .required('Day is required'),
  year: Yup.number()
    .typeError('Year must be a number')
    .min(1000, 'Year must be 4 digits')
    .integer('Year must be a whole number')
    .required('Year is required'),
});

export const makeMemorableDateValidationSchema = ({
  enhanceDateSchema = R.identity,
}: MemorableDateValidationSchemaInput) => {
  const baseDateSchema = Yup.date().typeError('Date is invalid');

  return memorableDateSchema.shape({
    composedDate: enhanceDateSchema(baseDateSchema),
  });
};

export type MemorableDate$Form = MemorableDate & {
  /** Composed Date is set automatically by FormikMemorableDateInput when all other fields are valid */
  composedDate?: Date;
};

const getMemorableDateFieldsPropFromFormik = (
  form: PgForm<any>,
  dateFieldName: string,
): MemorableDateInputProps['fields'] => {
  const value = form.values[dateFieldName];
  if (!value || !('day' in value) || !('month' in value) || !('year' in value))
    throw new InvariantError(
      `${dateFieldName} does not have the expected shape in the form data`,
    );

  const getSubfieldProps = (subfield: keyof MemorableDate) => {
    const fieldName = `${dateFieldName}.${subfield}`;
    const formikFieldProps = form.getFieldProps(fieldName);
    return {
      ...formikFieldProps,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const {value} = e.target;
        // Select fields have a different signature and need to be handled differently
        if (subfield === 'month') {
          // Select fields can validate on every change because input is constrained
          return form.setFieldValue(fieldName, value, true);
        }

        return formikFieldProps.onChange(e);
      },
    };
  };

  return {
    month: getSubfieldProps('month'),
    day: getSubfieldProps('day'),
    year: getSubfieldProps('year'),
  };
};

// Trying to do equality checks on dates will result in a re-render loop, so we'll build our own comparable version
const getComparableDate = (date?: Date | null) => {
  try {
    if (!date) return 'undefined';
    if (!isValid(date)) return 'invalid';
    return format(date, CommonDateFormats['01/20/2020']);
  } catch (e) {
    return 'parseError';
  }
};

const withoutComposedDate = R.omit(['composedDate']);

/**
 * A user-friendly input for memorable dates that cleanly integrates into an existing formik (such as birthdays or anniversaries).
 * This component implements the best practices for prompting users for memorable dates as outlined by the USWDS
 * @see https://designsystem.digital.gov/patterns/create-a-user-profile/date-of-birth/
 *
 * When the date components are all valid and parse to a valid date, this sets that date in `form[dateFieldName].composedDate` for convenience
 */
export function FormikMemorableDateInput<
  TDateFieldName extends string,
  TFormValue extends Record<TDateFieldName, MemorableDate$Form>,
>(
  props: Pick<MemorableDateInputProps, 'monthFieldPrefix'> & {
    form: PgForm<TFormValue>;
    dateFieldName: TDateFieldName;
  },
) {
  const {form, dateFieldName, monthFieldPrefix} = props;
  const fieldError = R.path([dateFieldName, 'composedDate'], form.errors) as
    | string
    | undefined;

  // When form errors change, check if the composed date should be updated
  // This is necessary because the composed date is derived from the subfields values and errors
  // Trying to do this in the subfield update handler will not have the updated error values
  React.useEffect(() => {
    const updateComposedDateField = (nextDate: Date | null) =>
      form.setFieldValue(
        `${dateFieldName}.composedDate`,
        // ?? is needed to coerce null to undefined
        nextDate ?? undefined,
        true,
      );

    const fieldValues = form.values[dateFieldName] as MemorableDate$Form;
    const touchedFields = withoutComposedDate(
      // @ts-expect-error
      form.touched[dateFieldName],
    ) as Partial<Record<keyof MemorableDate, boolean>>;

    const fieldsAreValid = memorableDateSchema.isValidSync(fieldValues);
    const allFieldsAreTouched = R.equals(touchedFields, {
      day: true,
      month: true,
      year: true,
    });
    const nextComposedDate = parseDateFromMemorableDate(fieldValues);
    const composedDateWouldChange =
      getComparableDate(fieldValues.composedDate) !==
      getComparableDate(nextComposedDate);

    // Never update the composed date if it hasn't changed
    if (!composedDateWouldChange) return;

    // If all fields are touched, then update the composed date regardless of field-level validity
    if (allFieldsAreTouched) {
      updateComposedDateField(nextComposedDate);
      return;
    }

    // If the fields are not all touched, only update the composed date if the fields are valid
    if (fieldsAreValid) {
      updateComposedDateField(nextComposedDate);
    }
  }, [form.values[dateFieldName]]);

  return (
    <MemorableDateInput
      fields={getMemorableDateFieldsPropFromFormik(form, dateFieldName)}
      error={fieldError}
      fieldId={dateFieldName}
      monthFieldPrefix={monthFieldPrefix}
    />
  );
}
